import React, { useState, useMemo } from "react";
import styled from "styled-components";

import Modal from "../../modal/Modal";
import Input from "../../form-item/Input";
import Button from "../../button/Button";
import IconButton from "../../button/IconButton";
import StatusIndicator from "../../status-indicator/StatusIndicator";
import { useUpdateMembershipMutation } from "../../../app/accounts/memberships";

const MAX_IP_COUNT = 6;

const IPV4_REGEX = /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/;

const FormContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
`;

const IpListItem = styled.div`
    width: 100%;
    padding: 12px 16px;
    border-radius: 4px;
    border: 1px solid var(--soremo-border);
    background-color: #fff;
    box-shadow: 2px 4px 8px 0px rgba(0, 0, 0, 0.05);
    display: flex;
    align-items: center;
    justify-content: space-between;

    &:hover {
        background-color: var(--soremo-background);
    }
`;

const IpRestrictionModal = ({ membership, projectId, onClose }) => {
    const [localIps, setLocalIps] = useState(membership.allowed_ips || []);
    const [newIp, setNewIp] = useState("");
    const [error, setError] = useState("");
    const [status, setStatus] = useState("idle");

    const [updateMembership] = useUpdateMembershipMutation();

    const isSaving = status === "saving";

    const isValidNewIp = useMemo(() => {
        const trimmed = newIp.trim();
        if (!trimmed) return false;
        if (!IPV4_REGEX.test(trimmed)) return false;
        if (localIps.includes(trimmed)) return false;
        if (localIps.length >= MAX_IP_COUNT) return false;
        return true;
    }, [newIp, localIps]);

    const showStatus = (nextStatus) => {
        setStatus(nextStatus);
        if (nextStatus === "saved" || nextStatus === "error") {
            setTimeout(() => setStatus("idle"), 1500);
        }
    };

    const handleAddIp = async () => {
        const trimmedIp = newIp.trim();
        if (!isValidNewIp) return;

        setError("");
        showStatus("saving");
        const nextIps = [...localIps, trimmedIp];
        try {
            await updateMembership({
                projectId,
                userId: membership.user_id,
                allowed_ips: nextIps,
            }).unwrap();
            setLocalIps(nextIps);
            setNewIp("");
            showStatus("saved");
        } catch (err) {
            setError(err?.data?.message || "IPアドレスの追加に失敗しました。");
            showStatus("error");
        }
    };

    const handleRemoveIp = async (ipToRemove) => {
        setError("");
        showStatus("saving");
        const nextIps = localIps.filter((ip) => ip !== ipToRemove);
        try {
            await updateMembership({
                projectId,
                userId: membership.user_id,
                allowed_ips: nextIps,
            }).unwrap();
            setLocalIps(nextIps);
            showStatus("saved");
        } catch (err) {
            setError(err?.data?.message || "IPアドレスの削除に失敗しました。");
            showStatus("error");
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleAddIp();
        }
    };

    return (
        <Modal
            onClose={onClose}
            title="IP制限"
            description="登録したIPアドレス以外からのログインを制限"
            size="default"
        >
            <FormContainer>
                {error && (
                    <div
                        className="bodytext-13"
                        style={{
                            color: "var(--soremo-green)",
                            padding: "8px 12px",
                            background: "var(--soremo-background)",
                            borderRadius: 4,
                        }}
                    >
                        {error}
                    </div>
                )}

                <div className="u-row u-gap8 u-w100">
                    <div style={{ flex: 1 }} onKeyDown={handleKeyDown}>
                        <Input
                            type="text"
                            placeholder="192.168.1.1"
                            value={newIp}
                            onChange={(e) => {
                                setNewIp(e.target.value);
                                setError("");
                            }}
                        />
                    </div>
                    <Button
                        theme="primary"
                        variant="filled"
                        size="small"
                        width="fit"
                        leftIcon="add"
                        onClick={handleAddIp}
                        disabled={!isValidNewIp || isSaving}
                    >
                        追加
                    </Button>
                </div>

                {localIps.length > 0 && (
                    <div className="u-col u-gap8 u-w100">
                        {localIps.map((ip) => (
                            <IpListItem key={ip}>
                                <span className="bodytext-13">{ip}</span>
                                <IconButton
                                    onClick={() => handleRemoveIp(ip)}
                                    disabled={isSaving} icon="delete" />
                            </IpListItem>
                        ))}
                    </div>
                )}

                <StatusIndicator status={status} inModal />
            </FormContainer>
        </Modal>
    );
};

export default IpRestrictionModal;
